import {
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { RequestInterceptor } from './types';
import { setSessionOutHeader } from '../utils/headers';
import { checkLoginSuccess } from '../utils/check-login';

export type BlockingRequestHandleParams = {
  /** 当前拦截的错误码 */
  errCode: string,
  /** 当前请求信息 */
  config: AxiosRequestConfig,
  /** 页面是否已登陆 */
  isLoggedIn: boolean;
};

/**
 * 登陆失效拦截处理初始化参数选项
 */
export type BlockingRequestOptions = {
  /** 登陆失败错误码列表 */
  errCodes: string[],
  /** 登录Promise方法等待拦截 */
  waitRequestHandle: (config: AxiosRequestConfig) => Promise<void>;
  /** 更新axios响应结果 */
  waitUpdateResponse?: (response: AxiosResponse, isLoggedIn: boolean) => Promise<AxiosResponse| null>;
  /** 登陆失效响应拦截后的处理 */
  waitResponseHandle: WaitResponseHandle,
};

/**
 * 登陆失败请求响应拦截后的处理方法
 */
export type WaitResponseHandle = (res: BlockingRequestHandleParams) => void;

/**
 * 登陆失效拦截处理类
 */
export default class BlockingRequestInterceptor implements RequestInterceptor {
  /**
   * 登陆失效错误码列表
   */
  errCodes: string[] = [];
  /**
   * 登陆失败请求响应拦截后的处理方法
   */
  waitResponseHandle!: WaitResponseHandle;
  /**
   * 登录Promise方法等待拦截
   * */
  waitRequestHandle: (config: AxiosRequestConfig) => Promise<void>;
  /**
   * 业务数据请求报错后，尝试重新更新响应数据
   */
  waitUpdateResponse?: (response: AxiosResponse, isLoggedIn: boolean) => Promise<AxiosResponse| null>;
  /**
   * 是否已经在处理登录态失效问题
   */
  isLoginOutProcessing = false;
  /**
   * 是否已经有登录成功
   */
  isLoggedIn = false;

  /**
   * 构造函数
   * @param options 处理选项
   */
  constructor(options: BlockingRequestOptions) {
    const { errCodes, waitResponseHandle, waitRequestHandle, waitUpdateResponse } = options;
    console.log('执行了 BlockingRequestInterceptor', options);

    if (errCodes.length === 0) {
      throw new Error('errCodes is emtpy');
    }
    this.errCodes = errCodes;
    this.waitRequestHandle = waitRequestHandle;
    this.waitResponseHandle = waitResponseHandle;
    this.waitUpdateResponse = waitUpdateResponse;
  }

  /**
   * 处理请求
   * @param config
   * @returns
   */
  async handleRequest(config: AxiosRequestConfig) {
    console.log('请求拦截 login-blocking requestConfig', config);
    await this.waitRequestHandle(config);
    return config;
  }
  /**
   *  解析接口响应数据
   * @param response
   * @returns
   */
  parseResponse(response: AxiosResponse) {
    return {
      response,
      retcode: response.data?.retcode || '' as string,
      config: response.config,
    };
  }

  /**
   * 通过接口返回的错误码检测登陆态是否已失效
   * @param retcode 接口返回错误码
   * @returns
   */
  checkSessionOut(retcode: string) {
    const { errCodes } = this;
    return errCodes.includes(retcode);
  }

  /**
   * 处理响应
   * @param response
   * @returns
   */
  async handleResponse(response: AxiosResponse) {
    const { waitResponseHandle } = this;
    let data = this.parseResponse(response);

    console.log('响应拦截 login-blocking response', data.response);

    // 检测到登陆超时，这里尝试重试请求更新下响应数据
    if (this.checkSessionOut(data.retcode) && this.waitUpdateResponse) {
      const updatedResponse = await this.waitUpdateResponse(data.response, this.isLoggedIn);
      if (updatedResponse) {
        // 用新的响应数据覆盖之前的返回结果
        data = this.parseResponse(updatedResponse);
      }
    }
    const { retcode, config } = data;
    if (checkLoginSuccess(retcode)) {
      this.isLoggedIn = true;
    }
    // 上面由于重拉了一次请求，接口返回数据大概率发生了变化，
    // 这里仍需要判断下重拉后是否仍登陆失效，失效的话走后续auth登陆
    if (this.checkSessionOut(retcode) && waitResponseHandle) {
      setSessionOutHeader(config);
      if (!this.isLoginOutProcessing) {
        setTimeout(() => {
          // 透传错误信息交给业务方处理
          waitResponseHandle({
            errCode: retcode,
            config,
            isLoggedIn: this.isLoggedIn,
          });
        });
        this.isLoginOutProcessing = true;
      }
    }
    return data.response;
  }
}

